import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Loader2,
  RefreshCw,
} from 'lucide-react';

import {
  api,
  type DomesticServiceRateSummary as DomesticServiceRateSummaryResult,
  type DomesticServiceRateSummaryRate,
  type DomesticServiceRateSummaryRow,
  type ServiceDefinitionFormValue,
} from '@/services/api';
import {
  effectiveDeckLabel,
  formatAverageRate,
  isLowestAverageIntrastate,
  summaryStatusLabel,
  summaryWarningText,
} from './domesticServiceRateSummaryState';

function localCalendarDate(): string {
  const now = new Date();
  return [
    String(now.getFullYear()).padStart(4, '0'),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : 'The domestic Service rate summary could not be loaded.';
}

function RateCell({
  rate,
  npanxxCount,
}: {
  rate: DomesticServiceRateSummaryRate;
  npanxxCount: number;
}) {
  return (
    <div className="text-right font-mono text-xs tabular-nums text-surface-900 dark:text-surface-100">
      {formatAverageRate(rate.average)}
      {npanxxCount > 0 && rate.numericCount !== npanxxCount && (
        <div className="mt-0.5 font-sans text-[10px] text-amber-700 dark:text-amber-300">
          {rate.numericCount.toLocaleString()} rated
        </div>
      )}
    </div>
  );
}

function StatusBadge({ row }: { row: DomesticServiceRateSummaryRow }) {
  const ready = row.status === 'ready';
  const warning = row.status === 'warning';
  return (
    <span
      title={summaryWarningText(row) || undefined}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold ${
        ready
          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
          : warning
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
            : 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
      }`}
    >
      {ready ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
      {summaryStatusLabel(row)}
    </span>
  );
}

interface DomesticServiceRateSummaryProps {
  disabled?: boolean;
  direction: 'inbound' | 'outbound';
}

export function DomesticServiceRateSummary({
  disabled = false,
  direction,
}: DomesticServiceRateSummaryProps) {
  const [services, setServices] = useState<ServiceDefinitionFormValue[]>([]);
  const [serviceDefinitionId, setServiceDefinitionId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(localCalendarDate);
  const [loadingServices, setLoadingServices] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<DomesticServiceRateSummaryResult | null>(null);

  const loadServices = useCallback(async () => {
    setLoadingServices(true);
    setError(null);
    try {
      const state = await api.carrierInitialization.getServiceDefinitions();
      const definitions = state?.definitions ?? [];
      setServices(definitions);
      setServiceDefinitionId((current) => current || definitions[0]?.id || '');
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoadingServices(false);
    }
  }, []);

  useEffect(() => {
    void loadServices();
  }, [loadServices]);

  const selectedService = useMemo(
    () => services.find((service) => service.id === serviceDefinitionId) ?? null,
    [serviceDefinitionId, services],
  );

  const compare = async () => {
    if (!serviceDefinitionId || !effectiveDate || disabled) return;
    setLoadingReport(true);
    setError(null);
    try {
      const loadSummary = direction === 'inbound'
        ? api.rateDecks.getDomesticCustomerServiceSummary
        : api.rateDecks.getDomesticServiceSummary;
      setReport(await loadSummary({
        serviceDefinitionId,
        providerCalendarEffectiveAt: effectiveDate,
      }));
    } catch (loadError) {
      setError(errorMessage(loadError));
      setReport(null);
    } finally {
      setLoadingReport(false);
    }
  };

  const isInbound = direction === 'inbound';
  const partyLabel = isInbound ? 'Customer' : 'Carrier';
  const deckScopeLabel = isInbound ? 'Inbound Customer' : 'Outbound Provider';

  return (
    <section
      aria-label={`Domestic ${partyLabel} Service rate summary`}
      className="mt-3 w-[min(72rem,calc(100vw-7rem))] max-w-full overflow-hidden rounded-xl border border-surface-200 bg-surface-50 shadow-sm dark:border-surface-700 dark:bg-surface-950"
    >
      <header className="border-b border-surface-200 bg-white px-4 py-3.5 dark:border-surface-800 dark:bg-surface-900">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300">
            <BarChart3 className="h-4 w-4" />
          </span>
          <div>
            <p className="text-[0.65rem] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-400">
              {isInbound ? 'Domestic customer pricing' : 'Domestic cost analysis'}
            </p>
            <h3 className="mt-1 text-base font-semibold text-surface-950 dark:text-white">{deckScopeLabel} Service Rate Summary</h3>
            <p className="mt-1 text-xs leading-5 text-surface-500 dark:text-surface-400">
              Compare saved {isInbound ? 'Customer ' : ''}Rate Decks for one Service and provider calendar date.
            </p>
          </div>
        </div>
      </header>

      <div className="grid gap-3 border-b border-surface-200 bg-white p-4 sm:grid-cols-[minmax(14rem,1fr)_11rem_auto] sm:items-end dark:border-surface-800 dark:bg-surface-900">
        <label className="block text-xs font-semibold text-surface-700 dark:text-surface-200">
          Service
          <select
            value={serviceDefinitionId}
            onChange={(event) => setServiceDefinitionId(event.target.value)}
            disabled={disabled || loadingServices || services.length === 0}
            className="mt-1.5 h-10 w-full rounded-lg border border-surface-300 bg-white px-3 text-sm text-surface-950 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 disabled:opacity-50 dark:border-surface-700 dark:bg-surface-950 dark:text-white"
          >
            {services.length === 0 && <option value="">No Services available</option>}
            {services.map((service) => (
              <option key={service.id} value={service.id}>{service.code} — {service.name}</option>
            ))}
          </select>
        </label>
        <label className="block text-xs font-semibold text-surface-700 dark:text-surface-200">
          Provider calendar date
          <input
            type="date"
            value={effectiveDate}
            onChange={(event) => setEffectiveDate(event.target.value)}
            disabled={disabled || loadingServices}
            className="mt-1.5 h-10 w-full rounded-lg border border-surface-300 bg-white px-3 text-sm text-surface-950 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 disabled:opacity-50 dark:border-surface-700 dark:bg-surface-950 dark:text-white"
          />
        </label>
        <button
          type="button"
          onClick={() => void compare()}
          disabled={disabled || loadingServices || loadingReport || !serviceDefinitionId || !effectiveDate}
          className="flex h-10 items-center justify-center gap-2 rounded-lg bg-surface-900 px-4 text-xs font-semibold text-white hover:bg-surface-700 disabled:opacity-50 dark:bg-white dark:text-surface-950 dark:hover:bg-surface-200"
        >
          {loadingReport && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Compare rates
        </button>
      </div>

      {error && (
        <div role="alert" className="flex items-start gap-2 border-b border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
          {!report && services.length === 0 && (
            <button type="button" onClick={() => void loadServices()} className="ml-auto flex shrink-0 items-center gap-1 font-semibold">
              <RefreshCw className="h-3 w-3" />Retry
            </button>
          )}
        </div>
      )}

      {!report && !error && (
        <div className="px-4 py-8 text-center text-xs text-surface-500 dark:text-surface-400">
          {loadingServices ? 'Loading Services…' : `Select a Service and date to compare applicable ${deckScopeLabel} Rate Decks.`}
        </div>
      )}

      {report && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-xs text-surface-600 dark:text-surface-300">
            <span><strong className="text-surface-900 dark:text-white">{selectedService?.code ?? 'Service'}</strong> · {report.rows.length} applicable trunk{report.rows.length === 1 ? '' : 's'}</span>
            <span>Effective {report.providerCalendarEffectiveAt.slice(0, 10)}</span>
          </div>
          {report.rows.length === 0 ? (
            <div className="border-t border-surface-200 px-4 py-8 text-center text-xs text-surface-500 dark:border-surface-800 dark:text-surface-400">
              No applicable domestic {deckScopeLabel} Rate Decks were found for this Service and date.
            </div>
          ) : (
            <div className="overflow-x-auto border-t border-surface-200 dark:border-surface-800">
              <table className="min-w-[62rem] w-full border-collapse text-left text-xs">
                <thead className="bg-surface-100 text-[10px] uppercase tracking-wide text-surface-500 dark:bg-surface-900 dark:text-surface-400">
                  <tr>
                    <th className="px-4 py-2.5 font-semibold">{partyLabel} / trunk</th>
                    <th className="px-3 py-2.5 font-semibold">Effective deck</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Codes</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Avg Interstate</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Avg Intrastate</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Avg Indeterminate</th>
                    <th className="px-4 py-2.5 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-200 bg-white dark:divide-surface-800 dark:bg-surface-950">
                  {report.rows.map((row) => (
                    <tr key={`${row.carrierId}:${row.connectorKey}:${row.providerRecordId}`}>
                      <td className="px-4 py-3 align-top">
                        <div className="font-semibold text-surface-950 dark:text-white">{row.carrierName}</div>
                        <div className="mt-0.5 text-[10px] text-surface-500 dark:text-surface-400">{isInbound ? 'Inbound' : 'Outbound'} trunk #{row.providerRecordId}</div>
                        {isLowestAverageIntrastate(row, report.rows) && (
                          <span className="mt-1.5 inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                            Lowest average Intrastate
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top text-surface-700 dark:text-surface-200">
                        <div>{effectiveDeckLabel(row)}</div>
                        {row.versionId && <div className="mt-0.5 max-w-40 truncate font-mono text-[10px] text-surface-400" title={row.versionId}>{row.versionId}</div>}
                      </td>
                      <td className="px-3 py-3 text-right align-top font-mono tabular-nums text-surface-900 dark:text-surface-100">{row.npanxxCount.toLocaleString()}</td>
                      <td className="px-3 py-3 align-top"><RateCell rate={row.rates.interstate} npanxxCount={row.npanxxCount} /></td>
                      <td className="bg-amber-50/60 px-3 py-3 align-top dark:bg-amber-950/10"><RateCell rate={row.rates.intrastate} npanxxCount={row.npanxxCount} /></td>
                      <td className="px-3 py-3 align-top"><RateCell rate={row.rates.indeterminate} npanxxCount={row.npanxxCount} /></td>
                      <td className="px-4 py-3 align-top"><StatusBadge row={row} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <footer className="border-t border-surface-200 bg-surface-100 px-4 py-3 text-[11px] leading-5 text-surface-600 dark:border-surface-800 dark:bg-surface-900 dark:text-surface-300">
            Averages use an unweighted arithmetic mean across distinct six-digit domestic NPANXX codes with a numeric rate. Missing rates are not zero. IntraLATA is excluded.
            {report.omittedNonApplicableCount > 0 && ` ${report.omittedNonApplicableCount} trunk${report.omittedNonApplicableCount === 1 ? ' was' : 's were'} omitted because no Rate Deck applied on this date.`}
          </footer>
        </div>
      )}
    </section>
  );
}
